/**
 * Página de apresentação e download.
 *
 * É a primeira coisa que alguém vê ao chegar no endereço sem estar autenticado,
 * e o trabalho dela é convencer em poucos segundos: o que dá para fazer aqui,
 * quanto custa e como começar.
 *
 * Quem lê esta página é quem vai conversar, não quem vai administrar o
 * servidor. Por isso nada de franquia de banda, custo por participante ou nome
 * de biblioteca: são coisas verdadeiras e importantes, só que para outra
 * pessoa, e no meio de uma decisão de trinta segundos elas só atrapalham.
 *
 * O download aponta para os arquivos servidos pela própria instalação, em
 * `/downloads`. Assim o instalador que a pessoa baixa é o da instância onde ela
 * vai entrar, e não um binário genérico apontando para outro servidor.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Download,
  Hash,
  Link2,
  Monitor,
  MonitorUp,
  Shield,
  Volume2,
  Waves,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatBytes } from '@nexus/shared'
import { Button } from '@/components/ui'

interface BuildInfo {
  version: string
  platforms: {
    id: 'windows' | 'mac' | 'linux'
    label: string
    filename: string
    url: string
    size: number
    available: boolean
  }[]
}

/** Detecta o sistema para destacar o download certo. */
function detectPlatform(): 'windows' | 'mac' | 'linux' {
  const agent = navigator.userAgent.toLowerCase()
  if (agent.includes('mac')) return 'mac'
  if (agent.includes('linux') && !agent.includes('android')) return 'linux'
  return 'windows'
}

export function LandingView() {
  const [builds, setBuilds] = useState<BuildInfo | null>(null)
  const [platform] = useState(detectPlatform)

  useEffect(() => {
    // Se o endpoint não existir ou não houver build publicada, a seção de
    // download some em vez de oferecer um link quebrado.
    void fetch('/api/downloads')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: BuildInfo | null) => setBuilds(data))
      .catch(() => setBuilds(null))
  }, [])

  const primary = builds?.platforms.find((p) => p.id === platform && p.available)
  const others = builds?.platforms.filter((p) => p.id !== platform && p.available) ?? []

  return (
    <div className="min-h-dvh bg-surface-base">
      <header className="sticky top-0 z-30 border-b border-line bg-surface-base/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-6">
          <div className="flex size-9 items-center justify-center rounded-lg bg-brand">
            <svg viewBox="0 0 32 32" className="size-5" fill="none" aria-hidden="true">
              <path
                d="M8 11.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-6.2L11 24v-3.5h-1a2 2 0 0 1-2-2v-7z"
                fill="#fff"
              />
            </svg>
          </div>
          <span className="text-lg font-bold text-content-primary">Nexus</span>

          <nav className="ml-auto flex items-center gap-1 sm:gap-2">
            <a
              href="#recursos"
              className="hidden px-3 py-2 text-sm text-content-secondary transition-colors hover:text-content-primary sm:block"
            >
              Recursos
            </a>
            <a
              href="#aplicativo"
              className="hidden px-3 py-2 text-sm text-content-secondary transition-colors hover:text-content-primary sm:block"
            >
              Aplicativo
            </a>
            <Link to="/entrar-conta">
              <Button variant="ghost" size="sm">
                Entrar
              </Button>
            </Link>
            <Link to="/cadastro">
              <Button size="sm">Criar conta</Button>
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-6 pt-20 pb-14 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-subtle px-3 py-1 text-[13px] font-medium text-brand">
            <BadgeCheck className="size-3.5" />
            Grátis para usar, sem anúncios
          </span>

          <h1 className="mx-auto mt-6 max-w-3xl text-4xl leading-[1.1] font-bold tracking-tight text-content-primary sm:text-5xl">
            Sua turma toda
            <br />
            no mesmo lugar
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-content-secondary">
            Canais de texto, chamadas de voz com som limpo e transmissão de tela em alta definição.
            Abre no navegador e leva um minuto para começar.
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/cadastro">
              <Button size="lg" className="h-12 px-7 text-[15px]">
                Criar conta grátis
                <ArrowRight className="size-4" />
              </Button>
            </Link>

            {primary ? (
              <a href={primary.url} download>
                <Button variant="outline" size="lg" className="h-12 px-6 text-[15px]">
                  <Download className="size-5" />
                  Baixar para {primary.label}
                </Button>
              </a>
            ) : (
              <Link to="/entrar-conta">
                <Button variant="outline" size="lg" className="h-12 px-6 text-[15px]">
                  Já tenho conta
                </Button>
              </Link>
            )}
          </div>

          {primary ? (
            <p className="mt-3 text-[13px] text-content-muted">
              Versão {builds?.version} · {formatBytes(primary.size)}
              {others.length > 0 ? (
                <>
                  {' · também para '}
                  {others.map((option, index) => (
                    <span key={option.id}>
                      {index > 0 ? ', ' : ''}
                      <a href={option.url} download className="text-brand hover:underline">
                        {option.label}
                      </a>
                    </span>
                  ))}
                </>
              ) : null}
            </p>
          ) : null}
        </section>

        {/* Prévia da interface, desenhada em SVG para não depender de captura
            de tela que envelhece a cada mudança visual. */}
        <section className="mx-auto max-w-5xl px-6 pb-16">
          <AppPreview />
        </section>

        {/* Uma faixa de argumentos curtos, para quem rolou a página sem parar
            para ler os cartões. */}
        <section className="border-y border-line bg-surface-sunken/40">
          <div className="mx-auto grid max-w-6xl gap-6 px-6 py-10 sm:grid-cols-3">
            <Destaque titulo="Não custa nada" texto="Sem plano pago e sem anúncio no meio da conversa." />
            <Destaque titulo="Sem instalar" texto="Abre direto no navegador, em qualquer computador." />
            <Destaque titulo="Convite por link" texto="Manda o link e a pessoa já está dentro." />
          </div>
        </section>

        <section id="recursos" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-6xl px-6">
            <h2 className="text-center text-2xl font-bold text-content-primary sm:text-3xl">
              Tudo o que a conversa precisa
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-center leading-relaxed text-content-secondary">
              Do recado rápido à chamada de duas horas mostrando a tela.
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Feature
                icon={<Volume2 />}
                title="Voz em um clique"
                description="Entre no canal e já está falando. Você vê quem está com a palavra e ajusta o volume de cada pessoa do seu jeito, sem incomodar ninguém."
              />
              <Feature
                icon={<MonitorUp />}
                title="Sua tela em alta definição"
                description="Mostre um jogo, uma planilha ou uma apresentação em até 1080p. Quem assiste regula o som da transmissão sem mexer na sua voz."
              />
              <Feature
                icon={<Waves />}
                title="Som limpo de verdade"
                description="O teclado, o ventilador e a conversa lá do fundo somem antes de chegar aos outros. Dá para testar o microfone antes de entrar."
              />
              <Feature
                icon={<Hash />}
                title="Conversas organizadas"
                description="Canais por assunto, respostas, reações, mensagens fixadas e arquivos de até 25 MB. O histórico inteiro fica ali, pesquisável pela rolagem."
              />
              <Feature
                icon={<Shield />}
                title="Cada um no seu lugar"
                description="Cargos decidem quem cria canal, quem modera e quem só participa. E cada pessoa pode ter um apelido diferente em cada servidor."
              />
              <Feature
                icon={<Link2 />}
                title="Chamar é fácil"
                description="Um link de convite basta. Quem recebe entra pelo navegador na hora, sem baixar nada e sem procurar por você numa lista."
              />
            </div>
          </div>
        </section>

        <section className="border-t border-line bg-surface-sunken/40 py-20">
          <div className="mx-auto max-w-4xl px-6">
            <h2 className="text-center text-2xl font-bold text-content-primary sm:text-3xl">
              Começar leva um minuto
            </h2>

            <ol className="mt-12 grid gap-8 sm:grid-cols-3">
              <Passo
                numero={1}
                titulo="Crie sua conta"
                texto="Nome, e-mail e senha. Só isso."
              />
              <Passo
                numero={2}
                titulo="Monte seu servidor"
                texto="Os canais de texto e voz já vêm prontos para usar."
              />
              <Passo
                numero={3}
                titulo="Chame a sua turma"
                texto="Copie o link do convite e mande para quem quiser."
              />
            </ol>

            <div className="mt-12 text-center">
              <Link to="/cadastro">
                <Button size="lg" className="h-12 px-7 text-[15px]">
                  Criar conta grátis
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section id="aplicativo" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-4xl px-6">
            <div className="rounded-2xl border border-line bg-surface-raised p-8 sm:p-10">
              <div className="flex flex-wrap items-start justify-between gap-8">
                <div className="max-w-md">
                  <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-brand-subtle text-brand">
                    <Monitor className="size-6" />
                  </div>

                  <h2 className="text-xl font-bold text-content-primary">
                    Vale instalar no computador?
                  </h2>
                  <p className="mt-2 leading-relaxed text-content-secondary">
                    O navegador dá conta de tudo. O aplicativo acrescenta o que uma aba não
                    consegue fazer.
                  </p>

                  {primary ? (
                    <a href={primary.url} download className="mt-6 inline-block">
                      <Button size="lg">
                        <Download className="size-4" />
                        Baixar para {primary.label}
                      </Button>
                    </a>
                  ) : (
                    /* Sem build publicada, dizer que está a caminho é melhor do
                       que um botão que não leva a lugar nenhum. */
                    <p className="mt-6 rounded-lg bg-surface-base px-4 py-3 text-[13px] leading-relaxed text-content-muted">
                      O aplicativo para computador está a caminho. Por enquanto, use pelo
                      navegador: tudo funciona por lá.
                    </p>
                  )}
                </div>

                <ul className="flex min-w-56 flex-col gap-3">
                  {[
                    'Silenciar por atalho, sem sair do que está fazendo',
                    'Escolher a janela exata para transmitir',
                    'Mandar o som do computador junto com a tela',
                    'Ícone na bandeja com o que ficou sem ler',
                  ].map((item) => (
                    <li key={item} className="flex gap-2.5 text-[14px] text-content-secondary">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-line py-20">
          <div className="mx-auto max-w-2xl px-6 text-center">
            <h2 className="text-2xl font-bold text-content-primary sm:text-3xl">
              Chame a sua turma hoje
            </h2>
            <p className="mt-3 leading-relaxed text-content-secondary">
              Crie a conta, monte o servidor e mande o convite. Não custa nada.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/cadastro">
                <Button size="lg" className="h-12 px-7 text-[15px]">
                  Criar conta grátis
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
              <Link to="/entrar-conta">
                <Button variant="ghost" size="lg" className="h-12 px-6 text-[15px]">
                  Já tenho conta
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-line py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 text-[13px] text-content-muted">
          <span>Nexus</span>
          <div className="flex gap-5">
            <Link to="/entrar-conta" className="transition-colors hover:text-content-secondary">
              Entrar
            </Link>
            <Link to="/cadastro" className="transition-colors hover:text-content-secondary">
              Criar conta
            </Link>
            <Link to="/entrar" className="transition-colors hover:text-content-secondary">
              Usar convite
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

function Destaque({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="text-center sm:text-left">
      <p className="font-semibold text-content-primary">{titulo}</p>
      <p className="mt-1 text-[14px] leading-relaxed text-content-secondary">{texto}</p>
    </div>
  )
}

function Passo({ numero, titulo, texto }: { numero: number; titulo: string; texto: string }) {
  return (
    <li className="text-center sm:text-left">
      <span className="inline-flex size-9 items-center justify-center rounded-full bg-brand text-[15px] font-bold text-white">
        {numero}
      </span>
      <h3 className="mt-4 font-semibold text-content-primary">{titulo}</h3>
      <p className="mt-1.5 text-[14px] leading-relaxed text-content-secondary">{texto}</p>
    </li>
  )
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-line bg-surface-base p-6 transition-colors hover:border-line-strong">
      <div
        className={cn(
          'mb-4 flex size-11 items-center justify-center rounded-xl',
          'bg-brand-subtle text-brand [&>svg]:size-5',
        )}
      >
        {icon}
      </div>
      <h3 className="font-semibold text-content-primary">{title}</h3>
      <p className="mt-1.5 text-[14px] leading-relaxed text-content-secondary">{description}</p>
    </div>
  )
}

/**
 * Prévia da interface em SVG.
 *
 * Uma captura de tela real precisaria ser refeita a cada ajuste visual e
 * envelheceria em silêncio. Esta versão usa os mesmos tokens de cor do tema, e
 * acompanha qualquer mudança de paleta sozinha.
 */
function AppPreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface-sunken shadow-2xl">
      <div className="flex h-9 items-center gap-2 border-b border-line bg-surface-deepest px-4">
        <span className="size-2.5 rounded-full bg-danger/70" />
        <span className="size-2.5 rounded-full bg-warning/70" />
        <span className="size-2.5 rounded-full bg-success/70" />
      </div>

      <svg viewBox="0 0 900 460" className="w-full" role="img" aria-label="Prévia da interface">
        <rect width="900" height="460" fill="var(--color-surface-base)" />

        {/* Barra de servidores */}
        <rect width="64" height="460" fill="var(--color-surface-deepest)" />
        <rect x="12" y="14" width="40" height="40" rx="12" fill="var(--color-brand)" />
        <rect x="4" y="26" width="4" height="16" rx="2" fill="var(--color-content-primary)" />
        {[70, 122, 174].map((y) => (
          <rect key={y} x="12" y={y} width="40" height="40" rx="20" fill="var(--color-surface-raised)" />
        ))}

        {/* Lista de canais */}
        <rect x="64" y="0" width="200" height="460" fill="var(--color-surface-sunken)" />
        <rect x="80" y="20" width="120" height="10" rx="5" fill="var(--color-content-secondary)" />
        <rect x="80" y="56" width="70" height="7" rx="3.5" fill="var(--color-content-muted)" />
        {[76, 106, 136].map((y, index) => (
          <g key={y}>
            {index === 0 ? (
              <rect x="72" y={y - 6} width="184" height="26" rx="6" fill="var(--color-surface-active)" />
            ) : null}
            <text x="84" y={y + 6} fontSize="13" fill="var(--color-content-muted)">
              #
            </text>
            <rect
              x="98"
              y={y}
              width={index === 0 ? 76 : index === 1 ? 60 : 88}
              height="8"
              rx="4"
              fill={index === 0 ? 'var(--color-content-primary)' : 'var(--color-content-muted)'}
            />
          </g>
        ))}

        <rect x="80" y="178" width="80" height="7" rx="3.5" fill="var(--color-content-muted)" />
        {[198, 246].map((y, index) => (
          <g key={y}>
            <circle cx="88" cy={y + 4} r="5" fill="var(--color-content-muted)" opacity="0.7" />
            <rect x="100" y={y} width="70" height="8" rx="4" fill="var(--color-content-muted)" />
            {index === 0 ? (
              <>
                <circle cx="104" cy={y + 24} r="8" fill="var(--color-success)" />
                <rect x="118" y={y + 20} width="54" height="7" rx="3.5" fill="var(--color-content-secondary)" />
                <circle cx="188" cy={y + 24} r="5" fill="var(--color-success)" opacity="0.5" />
              </>
            ) : null}
          </g>
        ))}

        {/* Painel do usuário */}
        <rect x="64" y="408" width="200" height="52" fill="var(--color-surface-deepest)" />
        <circle cx="90" cy="434" r="13" fill="var(--color-brand)" />
        <rect x="112" y="426" width="60" height="7" rx="3.5" fill="var(--color-content-primary)" />
        <rect x="112" y="438" width="40" height="6" rx="3" fill="var(--color-content-muted)" />

        {/* Área de conversa */}
        <rect x="264" y="0" width="446" height="40" fill="var(--color-surface-base)" />
        <line x1="264" y1="40" x2="710" y2="40" stroke="var(--color-line)" />
        <text x="288" y="26" fontSize="15" fill="var(--color-content-muted)">
          #
        </text>
        <rect x="302" y="16" width="64" height="9" rx="4.5" fill="var(--color-content-primary)" />

        {[
          { y: 72, w: 300, avatar: 'var(--color-success)' },
          { y: 140, w: 220, avatar: 'var(--color-warning)' },
          { y: 208, w: 340, avatar: 'var(--color-brand)' },
          { y: 276, w: 180, avatar: 'var(--color-danger)' },
        ].map((row) => (
          <g key={row.y}>
            <circle cx="296" cy={row.y + 12} r="16" fill={row.avatar} opacity="0.85" />
            <rect x="322" y={row.y} width="80" height="8" rx="4" fill="var(--color-content-primary)" />
            <rect x="410" y={row.y + 1} width="36" height="6" rx="3" fill="var(--color-content-muted)" />
            <rect x="322" y={row.y + 18} width={row.w} height="8" rx="4" fill="var(--color-content-secondary)" opacity="0.55" />
            <rect x="322" y={row.y + 32} width={row.w * 0.6} height="8" rx="4" fill="var(--color-content-secondary)" opacity="0.35" />
          </g>
        ))}

        {/* Campo de escrita */}
        <rect x="286" y="404" width="402" height="40" rx="10" fill="var(--color-surface-raised)" />
        <rect x="306" y="420" width="150" height="8" rx="4" fill="var(--color-content-muted)" />
        <rect x="650" y="414" width="24" height="20" rx="6" fill="var(--color-brand)" />

        {/* Lista de membros */}
        <rect x="710" y="0" width="190" height="460" fill="var(--color-surface-sunken)" />
        <rect x="726" y="20" width="158" height="24" rx="6" fill="var(--color-surface-deepest)" />
        <rect x="726" y="62" width="60" height="7" rx="3.5" fill="var(--color-content-muted)" />
        {[84, 120, 156, 192].map((y, index) => (
          <g key={y}>
            <circle cx="740" cy={y + 8} r="11" fill={['var(--color-brand)', 'var(--color-success)', 'var(--color-warning)', 'var(--color-danger)'][index]} opacity="0.85" />
            <circle cx="748" cy={y + 16} r="4" fill="var(--color-success)" stroke="var(--color-surface-sunken)" strokeWidth="2" />
            <rect x="760" y={y + 4} width={[70, 54, 82, 62][index]} height="8" rx="4" fill="var(--color-content-secondary)" />
          </g>
        ))}
      </svg>
    </div>
  )
}
